import { useEffect, useState } from 'react';
import { Box, Card, Heading, Text, TextButton } from '@zillow/constellation-10';
import CoinIcon from '../RewardsUpsellCard/CoinIcon';

type RewardsSummaryCardProps = {
  title: string;
  pointsValueDescription?: string;
  ctaText?: string;
  ctaUrl: string;
  points: number;
  maxPoints: number;
};

export const RewardsSummaryCard = ({
  title,
  pointsValueDescription,
  ctaText,
  ctaUrl,
  points,
  maxPoints,
}: RewardsSummaryCardProps) => {
  const clampedPoints = Math.max(points, 0);
  const percentage = maxPoints > 0 ? (clampedPoints / maxPoints) * 100 : 0;

  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    if (percentage > 0) {
      const frame = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(frame);
    }
  }, [percentage]);

  return (
    <Card elevated={false} css={{ flex: 1, padding: 'default' }}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        <Heading level={3} textStyle="heading-xs">
          {title}
        </Heading>
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
          <CoinIcon size={40} />
          <Text textStyle="heading-md">
            <strong>{clampedPoints.toLocaleString()}</strong> pts
          </Text>
        </Box>
        {pointsValueDescription && <Text textStyle="body-sm">{pointsValueDescription}</Text>}
        <Box
          role="progressbar"
          aria-valuenow={clampedPoints}
          aria-valuemin={0}
          aria-valuemax={maxPoints}
          aria-label={`${clampedPoints.toLocaleString()} of ${maxPoints.toLocaleString()} reward points earned`}
          css={{
            position: 'relative',
            height: '16px',
            backgroundColor: 'bg.softer',
            borderRadius: 'capsule',
            overflow: 'hidden',
          }}
        >
          <Box
            css={{
              position: 'absolute',
              top: '0',
              left: '0',
              height: '100%',
              minWidth: animated && percentage > 0 ? '16px' : '0',
              transition: 'width 0.8s ease-out',
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
              background:
                'linear-gradient(to right, token(colors.bg.express.inspire.hero), token(colors.bg.express.inspire.impact))',
            }}
            style={{
              width: animated && percentage > 0 ? `${percentage}%` : '0',
            }}
          />
        </Box>
        <TextButton textStyle="body-bold" tone="brand" css={{ alignSelf: 'flex-start' }} asChild>
          <a href={ctaUrl}>{ctaText ?? 'Go to rewards'}</a>
        </TextButton>
      </Box>
    </Card>
  );
};
